import { arrayOf, bool, func, number, string } from 'prop-types';
import { useIntl } from 'react-intl';

import Checkbox from 'components/common/Checkbox';
import MuiTooltip from 'components/common/MuiTooltip';
import routesPaths from 'constants/routesPaths';
import { forecastingPeriodShape, forecastingProjectShape } from 'constants/shapes';
import { routeWithProps } from 'utils/helpers';
import { formatFractionDigits, toCurrency } from 'utils/number';
import { getMarginPercentage } from 'utils/periodUtilities';

import EmptyState from './EmptyState';
import {
  Column,
  Row,
  Name,
  Label,
  ServiceType,
  Margin,
  ToggleColumn,
  ProbabilityColumn,
  ProbabilityInput,
  ProbabilityLabel
} from './styles';

function ProjectItem({
  project,
  width = 'auto',
  listMonth,
  showToggle,
  isIncluded,
  onToggle,
  winProbability,
  onProbabilityChange
}) {
  const intl = useIntl();
  const { id, name, serviceType, currentRevenue, currentCosts, forecastedSummaries } = project;
  const currentMargin = getMarginPercentage(currentRevenue, currentCosts);

  const summariesList = [];

  listMonth.map(item => {
    const itemPeriod = forecastedSummaries.find(
      element => element.year == item.year && element.month == item.month
    );

    summariesList.push(itemPeriod);
  });

  return (
    <Row aria-label={name} id={`project-${id}`} key={id} $isPipeline={showToggle}>
      {showToggle && (
        <ToggleColumn>
          <Checkbox value={isIncluded} onChange={() => onToggle?.(id)} variant="new" />
        </ToggleColumn>
      )}
      <Column gap="0.6rem" flexDirection="column" minWidth={width} alignItems="start">
        {name.length > 22 ? (
          <>
            <MuiTooltip>{name}</MuiTooltip>
            <Name
              to={routeWithProps(routesPaths.financialProjectsDetails, {
                id
              })}
            >
              {name}
            </Name>
          </>
        ) : (
          <Name
            to={routeWithProps(routesPaths.financialProjectsDetails, {
              id
            })}
          >
            {name}
          </Name>
        )}
        <ServiceType>
          {serviceType?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
        </ServiceType>
      </Column>
      {!showToggle && (
        <>
          <Column gap="0.4rem" maxWidth={width} flexDirection="column" alignItems="end">
            <Label strong>{intl.formatMessage({ id: 'common.revenue' })}:</Label>
            <Label strong>{intl.formatMessage({ id: 'common.cost' })}:</Label>
            <Label strong>{intl.formatMessage({ id: 'common.margin' })}:</Label>
          </Column>
          <Column gap="0.5rem" maxWidth={width} flexDirection="column" alignItems="start">
            <Label>{currentRevenue ? toCurrency(currentRevenue) : '-'}</Label>
            <Label>{currentCosts ? toCurrency(currentCosts) : '-'}</Label>
            <Margin margin={currentMargin}>
              {currentMargin ? `${formatFractionDigits(currentMargin, 2)}%` : '--'}
            </Margin>
          </Column>
        </>
      )}
      {showToggle && (
        <ProbabilityColumn
          onClick={e => {
            e.stopPropagation();
          }}
        >
          <ProbabilityInput
            type="number"
            min="0"
            max="100"
            value={winProbability ?? 60}
            onChange={e => {
              const val = Number(e.target.value);
              const clamped = Math.min(100, Math.max(0, Number.isFinite(val) ? val : 60));
              onProbabilityChange?.(id, clamped);
            }}
          />
          <ProbabilityLabel>%</ProbabilityLabel>
        </ProbabilityColumn>
      )}
      {summariesList.map((period, index) => {
        if (!period) {
          return (
            <Column key={index} gap="0.5rem" flexDirection="column" maxWidth={width}>
              <EmptyState />
            </Column>
          );
        }

        const { revenue, cost, id } = period;
        const margin = getMarginPercentage(revenue, cost);

        return (
          <Column key={id} gap="0.5rem" flexDirection="column" maxWidth={width}>
            <Label>{revenue ? toCurrency(revenue) : '-'}</Label>
            {!showToggle && (
              <>
                <Label>{cost ? toCurrency(cost) : '-'}</Label>
                <Margin margin={margin}>
                  {margin ? `${formatFractionDigits(margin, 2)}%` : '--'}
                </Margin>
              </>
            )}
          </Column>
        );
      })}
    </Row>
  );
}

ProjectItem.propTypes = {
  project: forecastingProjectShape.isRequired,
  width: string,
  listMonth: arrayOf(forecastingPeriodShape).isRequired,
  showToggle: bool,
  isIncluded: bool,
  onToggle: func,
  winProbability: number,
  onProbabilityChange: func
};

export default ProjectItem;
